import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Col, Row, Table } from 'antd'

import { ExpenseAttachment } from 'core/application/docReviewDashboard/expenses/expenseAttachment'
import { SearchPremiumsFilter, searchPremiums } from 'core/application/expenses/searchPremiumsQuery'

import Card from '../../common/components/dataDisplay/Card'
import notification from '../../common/components/feedback/Notification'
import { parseZoneFormat } from '../../common/dateFormatters'
import useQueryRequest from '../../common/hooks/useQueryRequest'
import { formatMoney } from '../../common/moneyFormatter'
import { ROUTE_PATHS } from '../../common/routePaths'
import ExpenseReviewIcon from '../common/ExpenseReviewStatusIcon'
import ViewExpenseAttachment from '../common/ViewExpenseAttachment'
import SearchPremiumForm, { SearchPremiumFormValues } from './searchPremiumForm'

const SearchPremiums: React.FC = () => {
  const { t: translate } = useTranslation()
  const history = useHistory()

  const [viewAttachments, setViewAttachments] = useState<boolean>(false)
  const [activeModalAttachments, setActiveModalAttachments] = useState<ExpenseAttachment[]>([])
  const [filter, setFilter] = useState<SearchPremiumsFilter>({
    orderColumn: 'LastModifiedAt',
    pageNumber: 1,
    pageSize: 25,
    orderDescending: true,
  })
  const [pagedItems, isPagedItemsLoading, sendSearchPremiums] = useQueryRequest(null)

  const columns: any = [
    {
      title: translate('doc-review-dashboard.search-expenses.expense-id'),
      dataIndex: 'expenseNumber',
      key: 'expenseNumber',
      align: 'center',
      sorter: false,
      render: (text: string, record: any) => {
        return (
          <a onClick={() => history.push(`${ROUTE_PATHS.DOC_REVIEW_PREMIUMS}/${record.id}`)}>{record.expenseNumber}</a>
        )
      },
    },
    {
      title: translate('doc-review-dashboard.search-expenses.last-modified'),
      dataIndex: 'lastModifiedAt',
      key: 'lastModifiedAt',
      sorter: false,
      render: (text: string, record: any) => parseZoneFormat(record.lastModifiedAt),
    },
    {
      title: translate('doc-review-dashboard.search-expenses.full-name'),
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: false,
    },
    {
      title: 'Start date',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: false,
      render: (text: string, record: any) => parseZoneFormat(record.startDate),
    },
    {
      title: 'End date',
      dataIndex: 'endDate',
      key: 'endDate',
      sorter: false,
      render: (text: string, record: any) => parseZoneFormat(record.endDate),
    },
    {
      title: translate('doc-review-dashboard.search-expenses.company'),
      dataIndex: 'companyName',
      key: 'companyName',
      sorter: false,
    },
    {
      title: translate('doc-review-dashboard.search-expenses.email'),
      dataIndex: 'email',
      key: 'email',
      sorter: false,
    },
    {
      title: translate('doc-review-dashboard.search-expenses.status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      sorter: false,
      render: (text: string, record: any) => <ExpenseReviewIcon status={record.status} />,
    },
    {
      title: translate('doc-review-dashboard.search-expenses.expense-amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      sorter: false,
      render: (amount: number, record: any) => formatMoney(amount),
    },
    {
      title: translate('doc-review-dashboard.search-expenses.actions'),
      dataIndex: '',
      key: '',
      align: 'center',
      sorter: false,
      render: (text: string, record: any) => (
        <span
          className="color-blue-6 cursor_pointer"
          onClick={() => {
            setActiveModalAttachments(record.attachments)
            setViewAttachments(!viewAttachments)
          }}
        >
          {translate('general.view')}
        </span>
      ),
    },
  ]

  useEffect(() => {
    ;(async () => {
      if (filter.searchValue || filter.startDate || filter.lastModifiedAt) {
        await sendSearchPremiums(() => searchPremiums(filter))
      }
    })()
  }, [filter])

  const onSearch = (searchValue: SearchPremiumFormValues) => {
    if (searchValue.inputSearch && searchValue.inputSearch?.length < 1) {
      notification.open({
        type: 'error',
        message: translate('general.search-length'),
      })
      return
    }

    setFilter({
      ...filter,
      searchValue: searchValue.inputSearch,
      startDate: searchValue.startDate,
      lastModifiedAt: searchValue.lastModifiedAt,
    })
  }

  const onTableChange = (pagination: any, filters: any, sorter: any) => {
    setFilter({
      ...filter,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'createdAt',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  return (
    <Card title={'Search Premiums'}>
      <Row>
        <Col span={18}>
          <SearchPremiumForm onSearch={onSearch} loading={isPagedItemsLoading} />
        </Col>
      </Row>

      {(filter.searchValue || filter.startDate || filter.lastModifiedAt) && (
        <Row className="mt-16">
          <Col span={24}>
            <h5>
              {pagedItems?.totalItemCount} {translate('general.search-result-text')} "{filter.searchValue ?? ''}{' '}
              {filter.startDate ?? ''}"
            </h5>
          </Col>

          <Col span={24}>
            {pagedItems?.totalItemCount > 0 && (
              <Table
                dataSource={pagedItems?.items}
                onChange={onTableChange}
                loading={isPagedItemsLoading}
                columns={columns}
                pagination={{
                  total: pagedItems?.totalItemCount,
                  pageSize: pagedItems?.pageSize,
                  current: pagedItems?.pageNumber,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '30', '40'],
                }}
              />
            )}

            <ViewExpenseAttachment
              attachments={activeModalAttachments}
              visible={viewAttachments}
              onClose={() => setViewAttachments(false)}
            />
          </Col>

          <Col>
            {pagedItems?.totalItemCount === 0 && !isPagedItemsLoading && (
              <div className="gray_children">
                <h4> {translate('doc-review-dashboard.search-expenses.search-tips.title')}</h4>
                <span>{translate('doc-review-dashboard.search-expenses.search-tips.insensitive')}</span>
                <br />
                <span>{translate('doc-review-dashboard.search-expenses.search-tips.quoted')}</span>
                <br />
                <span>{translate('doc-review-dashboard.search-expenses.search-tips.and-terms')}</span>
                <br />
                <span>{translate('doc-review-dashboard.search-expenses.search-tips.not-terms')}</span>
                <br />
                <span>{translate('doc-review-dashboard.search-expenses.search-tips.group-terms')}</span>
                <br />
                <span>{translate('doc-review-dashboard.search-expenses.search-tips.prefix-terms')}</span>
              </div>
            )}
          </Col>
        </Row>
      )}
    </Card>
  )
}

export default SearchPremiums
